import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, Sparkles, ShieldCheck, ArrowRight } from "lucide-react";
import { toast } from "sonner";

import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import EnterpriseContactModal from "@/components/EnterpriseContactModal";
import SEO from "@/components/SEO";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ROISimulator from "@/components/ROISimulator";
// @deprecated F12.1 — legacy Footer only on rollback (REACT_APP_HEADER_V4_ENABLED=false).
import Footer from "@/components/Footer";
import HeaderV4 from "@/components/header-v4/HeaderV4";
import FooterV4 from "@/components/footer-v4/FooterV4";

const V4_ENABLED = (process.env.REACT_APP_HEADER_V4_ENABLED ?? "true") !== "false";


const HIGHLIGHT_PLAN = "pro";

export default function Pricing() {
    const { user } = useAuth();
    const { t } = useTranslation();
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [checkoutBusy, setCheckoutBusy] = useState(null);
    const [enterpriseOpen, setEnterpriseOpen] = useState(false);
    const nav = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const data = await api.plansPublic();
                setPlans(data.plans || []);
            } catch (e) {
                toast.error(e.message || "Failed to load plans");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const [interval, setInterval] = useState("monthly");

    const startCheckout = async (planId) => {
        if (!user) {
            nav(`/?intent=upgrade&plan=${planId}`);
            return;
        }
        setCheckoutBusy(planId);
        try {
            const { url } = await api.subscriptionCheckout(planId, interval);
            window.location.href = url;
        } catch (e) {
            toast.error(e.message || "Checkout error");
        } finally {
            setCheckoutBusy(null);
        }
    };

    return (
        <div className="min-h-screen bg-[var(--color-bg)] text-text-main">
            <SEO
                title={t("nav.pricing")}
                description={t("pricing.subtitle")}
                canonical="/pricing"
            />
            {/* Header — F12.1 chrome swap: HeaderV4 by default, legacy
                inline nav kept behind REACT_APP_HEADER_V4_ENABLED=false. */}
            {V4_ENABLED ? (
                <HeaderV4 />
            ) : (
                <header className="sticky top-0 z-30 border-b border-[var(--color-border)] bg-[var(--color-bg)]/85 backdrop-blur-xl">
                    <div className="mx-auto max-w-6xl flex items-center justify-between px-6 py-4">
                        <Link
                            to="/"
                            data-testid="pricing-header-logo"
                            className="flex items-center gap-2 group"
                        >
                            <span className="h-8 w-8 rounded-lg gradient-bg-cta grid place-items-center shadow-md">
                                <span className="text-[13px] font-bold text-white">G</span>
                            </span>
                            <span className="text-sm font-semibold tracking-tight">
                                Global Lead Suite
                            </span>
                        </Link>
                        <div className="flex items-center gap-3">
                            <Link
                                to="/blog"
                                data-testid="pricing-header-blog"
                                className="text-sm text-text-secondary hover:text-text-main transition-colors"
                            >
                                {t("nav.blog")}
                            </Link>
                            <LanguageSwitcher />
                        </div>
                    </div>
                </header>
            )}

            <div className="px-6 py-16 max-w-6xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center"
                >
                    <span
                        data-testid="pricing-eyebrow"
                        className="inline-flex items-center gap-2 rounded-full border border-[var(--color-border)] bg-[var(--color-card)] px-3 py-1 text-[10px] uppercase tracking-widest text-text-secondary"
                    >
                        <ShieldCheck className="h-3 w-3 text-[var(--color-secondary)]" />
                        {t("pricing.subtitle")}
                    </span>
                    <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight" data-testid="pricing-h1">
                        {t("pricing.title")}
                    </h1>
                    <p className="mt-4 text-base text-text-secondary max-w-2xl mx-auto">
                        {t("pricing.subtitle")}
                    </p>
                </motion.div>

                <div
                    data-testid="pricing-billing-toggle"
                    className="mt-8 inline-flex items-center gap-2 rounded-full border border-[var(--color-border)] bg-[var(--color-card)] p-1"
                >
                    <button
                        onClick={() => setInterval("monthly")}
                        data-testid="billing-toggle-monthly"
                        className={`px-4 py-1.5 text-xs font-medium rounded-full transition-colors ${
                            interval === "monthly"
                                ? "bg-white hover:bg-[#EDEDED] text-black"
                                : "text-text-secondary hover:text-text-main"
                        }`}
                    >
                        {t("pricing.monthly")}
                    </button>
                    <button
                        onClick={() => setInterval("annual")}
                        data-testid="billing-toggle-annual"
                        className={`px-4 py-1.5 text-xs font-medium rounded-full transition-colors flex items-center gap-1.5 ${
                            interval === "annual"
                                ? "bg-white hover:bg-[#EDEDED] text-black"
                                : "text-text-secondary hover:text-text-main"
                        }`}
                    >
                        {t("pricing.annual")}
                        <span className="text-[9px] uppercase font-bold bg-[var(--color-success)]/20 text-[var(--color-success)] px-1.5 py-0.5 rounded">
                            -20%
                        </span>
                    </button>
                </div>

                <div
                    data-testid="pricing-grid"
                    className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4"
                >
                    {loading && (
                        <div className="col-span-full text-center text-text-secondary py-16">
                            {t("pricing.loading")}
                        </div>
                    )}
                    {plans.map((p) => (
                        <PlanCard
                            key={p.id}
                            plan={p}
                            interval={interval}
                            highlight={p.id === HIGHLIGHT_PLAN}
                            currentPlan={user?.plan_id}
                            busy={checkoutBusy === p.id}
                            onChoose={() => {
                                if (p.is_contact_sales) setEnterpriseOpen(true);
                                else startCheckout(p.id);
                            }}
                        />
                    ))}
                </div>

                <ComparisonTable plans={plans} />

                <ROISimulator />

                <FaqBlock />
            </div>

            {V4_ENABLED ? <FooterV4 /> : <Footer />}

            <EnterpriseContactModal
                open={enterpriseOpen}
                onClose={() => setEnterpriseOpen(false)}
            />
        </div>
    );
}

function PlanCard({ plan, interval = "monthly", highlight, currentPlan, busy, onChoose }) {
    const { t } = useTranslation();
    const isCurrent = plan.id === currentPlan;
    const cta = plan.is_contact_sales
        ? t("pricing.contact_sales")
        : plan.id === "free"
          ? t("pricing.start_free_cta")
          : isCurrent
            ? t("pricing.current_plan")
            : t("pricing.select_plan");
    const isAnnual = interval === "annual";
    const monthlyEquivalent = isAnnual
        ? Math.round(((plan.price_annual_eur || 0) / 12) * 10) / 10
        : plan.price_monthly_eur;
    return (
        <motion.div
            data-testid={`plan-card-${plan.id}`}
            whileHover={{ y: -2 }}
            className={`relative rounded-2xl border p-6 flex flex-col ${
                highlight
                    ? "border-[var(--color-primary)] shadow-[0_0_0_1px_rgba(255, 255, 255,0.3)] bg-gradient-to-b from-[var(--color-primary)]/10 to-[var(--color-card)]"
                    : "border-[var(--color-border)] bg-[var(--color-card)]"
            }`}
        >
            {highlight && (
                <span className="absolute -top-3 left-6 rounded-full bg-white hover:bg-[#EDEDED] px-3 py-1 text-[10px] font-medium uppercase tracking-widest text-black">
                    {t("pricing.most_popular")}
                </span>
            )}
            <h3 className="text-lg font-semibold">{plan.name}</h3>
            <div className="mt-3">
                {plan.is_contact_sales ? (
                    <span className="text-2xl font-semibold">Custom</span>
                ) : plan.price_monthly_eur === 0 ? (
                    <span className="text-2xl font-semibold">$0</span>
                ) : isAnnual ? (
                    <>
                        <span className="text-3xl font-semibold tabular-nums" data-testid={`plan-price-annual-${plan.id}`}>
                            ${monthlyEquivalent}
                        </span>
                        <span className="text-xs text-text-secondary">/mo</span>
                        <p className="mt-1 text-[10px] text-text-secondary">
                            {t("pricing.billed_annually", { total: plan.price_annual_eur })}
                        </p>
                    </>
                ) : (
                    <>
                        <span className="text-3xl font-semibold tabular-nums">
                            ${plan.price_monthly_eur}
                        </span>
                        <span className="text-xs text-text-secondary">/mo</span>
                    </>
                )}
            </div>
            <p className="mt-1 text-[11px] text-text-secondary">
                {plan.credits_per_month.toLocaleString()} {t("pricing.col_credits_per_month").toLowerCase()} · {t("pricing.col_hourly_cap").toLowerCase()}{" "}
                {plan.hourly_credit_cap.toLocaleString()}
            </p>

            <ul className="mt-4 space-y-1.5 text-[13px] text-text-secondary flex-1">
                {plan.features_bullet?.map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                        <Check className="h-3.5 w-3.5 text-[var(--color-success)] mt-0.5 shrink-0" />
                        <span>{f}</span>
                    </li>
                ))}
            </ul>

            <button
                data-testid={`plan-cta-${plan.id}`}
                disabled={isCurrent || busy}
                onClick={onChoose}
                className={`mt-6 inline-flex justify-center items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium transition disabled:opacity-40 disabled:cursor-not-allowed ${
                    highlight
                        ? "bg-white hover:bg-[#EDEDED] text-black hover:opacity-90"
                        : "border border-[var(--color-border)] text-text-main hover:border-[var(--color-primary)]/40"
                }`}
            >
                {busy ? "…" : cta}
                {!isCurrent && !busy && <ArrowRight className="h-3.5 w-3.5" />}
            </button>
        </motion.div>
    );
}

function ComparisonTable({ plans }) {
    const { t } = useTranslation();
    if (!plans.length) return null;
    const paid = plans.filter((p) => !p.is_contact_sales);
    const rows = [
        {
            label: t("pricing.col_credits_per_month"),
            fn: (p) => p.credits_per_month.toLocaleString(),
        },
        {
            label: t("pricing.col_hourly_cap"),
            fn: (p) => `${p.hourly_credit_cap.toLocaleString()}/h`,
        },
        {
            label: t("pricing.col_lead_search"),
            fn: (p) => p.limits?.lead_search_batch_max,
        },
        {
            label: t("pricing.col_active_campaigns"),
            fn: (p) => p.limits?.active_campaigns_max,
        },
        {
            label: t("pricing.col_saved_leads"),
            fn: (p) => p.limits?.saved_leads_max?.toLocaleString(),
        },
        {
            label: t("pricing.col_ai_compose"),
            fn: (p) => p.limits?.ai_compose_per_day,
        },
        {
            label: t("pricing.col_modules"),
            fn: (p) =>
                p.modules.includes("*")
                    ? "All"
                    : p.modules.map((m) => m.replace(/_/g, " ")).join(", "),
        },
    ];
    return (
        <div className="mt-24" data-testid="pricing-compare">
            <h2 className="text-lg font-semibold text-text-main">{t("pricing.compare_plans")}</h2>
            <div className="mt-4 overflow-x-auto rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)]">
                <table className="min-w-full text-sm">
                    <thead>
                        <tr className="text-left text-[10px] uppercase tracking-widest text-text-secondary border-b border-[var(--color-border)]">
                            <th className="px-4 py-3">{t("pricing.feature_column")}</th>
                            {paid.map((p) => (
                                <th key={p.id} className="px-4 py-3">
                                    {p.name}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr
                                key={row.label}
                                className="border-b border-[var(--color-border)] last:border-0"
                            >
                                <td className="px-4 py-3 text-text-secondary">{row.label}</td>
                                {paid.map((p) => (
                                    <td key={p.id} className="px-4 py-3">
                                        {row.fn(p) ?? "—"}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function FaqBlock() {
    const { t } = useTranslation();
    const faqs = [
        { q: t("pricing.faq_q_cancel"), a: t("pricing.faq_a_cancel") },
        { q: t("pricing.faq_q_cap"), a: t("pricing.faq_a_cap") },
        { q: t("pricing.faq_q_extra"), a: t("pricing.faq_a_extra") },
        { q: t("pricing.faq_q_currency"), a: t("pricing.faq_a_currency") },
    ];
    return (
        <div className="mt-24" data-testid="pricing-faq">
            <h2 className="text-lg font-semibold">{t("pricing.faq_title")}</h2>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                {faqs.map((f, i) => (
                    <div
                        key={i}
                        className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4"
                    >
                        <p className="text-sm font-medium">{f.q}</p>
                        <p className="mt-1 text-xs text-text-secondary leading-relaxed">
                            {f.a}
                        </p>
                    </div>
                ))}
            </div>
            <p className="mt-8 text-xs text-text-secondary text-center">
                {t("pricing.affiliate_teaser_prefix")}{" "}
                <Link
                    to="/affiliate"
                    className="text-[var(--color-secondary)] hover:underline"
                >
                    {t("pricing.affiliate_teaser_cta")}
                </Link>{" "}
                {t("pricing.affiliate_teaser_suffix")}
            </p>
        </div>
    );
}
